/**
 * BriefcaseView — Agent Document Workspace
 *
 * Features:
 * - Realtime Firestore data via useBriefcase hook
 * - Grid + list toggle, live search (name/tags/preview), sort
 * - Category sidebar with live counts
 * - ⌘K command bar with Web Speech API voice input
 * - Document detail modal with conversation provenance + 5 actions
 * - Re-generate wired to Firestore (toggles _regenerating + status)
 * - Delete wired to Firestore
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  FileText, FileSpreadsheet, Mail, Presentation,
  Search, Grid3X3, List, SortDesc, Mic, MicOff,
  Send, Download, Link2, Trash2, RefreshCw, Archive,
  Copy, ChevronRight, X, Sparkles, Clock, Tag, MessageSquare,
  Command, FolderOpen, Plus, BarChart3, FileBox, Pencil,
} from 'lucide-react';
import { useBriefcase } from '../hooks/useBriefcase';
import type { BriefcaseDoc, DocStatus, FileType } from '../hooks/useBriefcase';

// ─── Types ────────────────────────────────────────────────────────────────────
type SortKey = 'date' | 'name' | 'size';
type ViewMode = 'grid' | 'list';

interface CategoryDef { id: string; label: string; icon: React.ReactNode; }

// ─── Voice STT availability ───────────────────────────────────────────────────
// Works on Chrome, Edge end Safari. Falls back gracefully on Firefox.
const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? ((window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition ?? null)
    : null;
const SPEECH_SUPPORTED = SpeechRecognitionAPI !== null;

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES: CategoryDef[] = [
  { id: 'all',           label: 'All',       icon: <FolderOpen   size={15} /> },
  { id: 'reports',       label: 'Reports',   icon: <BarChart3    size={15} /> },
  { id: 'emails',        label: 'Emails',    icon: <Mail         size={15} /> },
  { id: 'pdfs',          label: 'PDFs',      icon: <FileText     size={15} /> },
  { id: 'analyses',      label: 'Analyses',  icon: <Search       size={15} /> },
  { id: 'presentations', label: 'Decks',     icon: <Presentation size={15} /> },
  { id: 'drafts',        label: 'Drafts',    icon: <Pencil       size={15} /> },
  { id: 'archived',      label: 'Archived',  icon: <Archive      size={15} /> },
];

const STATUS_CFG: Record<DocStatus, { bg: string; text: string; label: string }> = {
  final:    { bg: 'rgba(34,197,94,0.12)',  text: '#22c55e', label: 'Final'    },
  draft:    { bg: 'rgba(250,204,21,0.12)', text: '#facc15', label: 'Draft'    },
  sent:     { bg: 'rgba(96,165,250,0.12)', text: '#60a5fa', label: 'Sent'     },
  archived: { bg: 'rgba(148,163,184,0.1)', text: '#94a3b8', label: 'Archived' },
};

// ─── File type accent colors ──────────────────────────────────────────────────
const FILE_TYPE_COLORS: Record<FileType, { accent: string; glow: string }> = {
  pdf:  { accent: '#f87171', glow: 'rgba(248,113,113,.18)' },
  xlsx: { accent: '#4ade80', glow: 'rgba(74,222,128,.18)' },
  docx: { accent: '#60a5fa', glow: 'rgba(96,165,250,.18)' },
  pptx: { accent: '#fb923c', glow: 'rgba(251,146,60,.18)' },
  eml:  { accent: '#c084fc', glow: 'rgba(192,132,252,.18)' },
};

// ─── Tag color system (hash-based) ────────────────────────────────────────────
const TAG_PALETTES = [
  { bg: 'rgba(99,102,241,0.10)',  text: '#818cf8' },  // indigo
  { bg: 'rgba(16,185,129,0.10)',  text: '#34d399' },  // emerald
  { bg: 'rgba(245,158,11,0.10)',  text: '#f59e0b' },  // amber
  { bg: 'rgba(244,63,94,0.10)',   text: '#fb7185' },  // rose
  { bg: 'rgba(14,165,233,0.10)',  text: '#38bdf8' },  // sky
  { bg: 'rgba(168,85,247,0.10)',  text: '#a78bfa' },  // violet
];
function tagColor(tag: string) {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_PALETTES[Math.abs(hash) % TAG_PALETTES.length];
}

const FILE_ICONS: Record<FileType, React.ReactNode> = {
  pdf:  <FileText        size={20} color="#f87171" />,
  xlsx: <FileSpreadsheet size={20} color="#4ade80" />,
  docx: <FileBox         size={20} color="#60a5fa" />,
  pptx: <Presentation    size={20} color="#fb923c" />,
  eml:  <Mail            size={20} color="#c084fc" />,
};

const FILE_ICONS_LG: Record<FileType, React.ReactNode> = {
  pdf:  <FileText        size={32} color="#f87171" />,
  xlsx: <FileSpreadsheet size={32} color="#4ade80" />,
  docx: <FileBox         size={32} color="#60a5fa" />,
  pptx: <Presentation    size={32} color="#fb923c" />,
  eml:  <Mail            size={32} color="#c084fc" />,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fuzzyMatch(text: string, q: string) {
  return text.toLowerCase().includes(q.toLowerCase());
}

function formatDate(dateStr: string): string {
  const d    = new Date(dateStr);
  const now  = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7)  return `${diff}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getCommandResponse(cmd: string): string {
  const c = cmd.toLowerCase();
  if (c.includes('send') || c.includes('envoie'))
    return '📤 Ready to send. I found the most recent matching document. Confirm recipient and I\'ll dispatch it.';
  if (c.includes('compare') || c.includes('diff'))
    return '🔍 Comparison mode. Select 2 documents to generate a structured diff analysis.';
  if (c.includes('merge') || c.includes('intègre') || c.includes('integrate'))
    return '🔗 Integration mode. Which documents should I combine? I\'ll extract key sections and compose a new document.';
  if (c.includes('pdf') || c.includes('export'))
    return '📄 Export ready. Select a document or batch-export your current filtered view.';
  if (c.includes('archive'))
    return '🗄️ Archive mode. Select documents to archive, or say "archive all drafts older than 30 days".';
  if (c.includes('summary') || c.includes('résumé'))
    return '📊 Briefcase summarised. I\'ll count, categorise and surface the key trends across your documents.';
  if (c.includes('regenerate') || c.includes('redo') || c.includes('refait'))
    return '♻️ Re-generation mode. Select a document and I\'ll reproduce it with updated data based on the original instruction.';
  return `🔎 Searching your Briefcase for "${cmd}"…`;
}

// ─── Main Component ───────────────────────────────────────────────────────────
interface Props { onNavigate?: (view: string) => void; }

export default function BriefcaseView({ onNavigate: _onNavigate }: Props) {
  // ── Data layer ──
  const {
    documents,
    loading,
    updateDocument,
    deleteDocument,
    regenerateDocument,
  } = useBriefcase();

  // ── UI state ──
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery,    setSearchQuery]    = useState('');
  const [selectedDoc,    setSelectedDoc]    = useState<BriefcaseDoc | null>(null);
  const [viewMode,       setViewMode]       = useState<ViewMode>('grid');
  const [sortBy,         setSortBy]         = useState<SortKey>('date');
  const [showCmdBar,     setShowCmdBar]     = useState(false);
  const [cmdText,        setCmdText]        = useState('');
  const [cmdResponse,    setCmdResponse]    = useState('');
  const [processing,     setProcessing]     = useState(false);
  const [animateIn,      setAnimateIn]      = useState(false);
  const [hoveredId,      setHoveredId]      = useState<string | null>(null);
  const [showActions,    setShowActions]    = useState<string | null>(null);
  const [isListening,    setIsListening]    = useState(false);

  const cmdRef    = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const recognizerRef = useRef<any>(null);

  useEffect(() => { setAnimateIn(true); }, []);

    // — Close dropdown on outside click —
    useEffect(() => {
      if (!showActions) return;
      const close = () => setShowActions(null);
      document.addEventListener('click', close);
      return () => document.removeEventListener('click', close);
    }, [showActions]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowCmdBar(true);
        setTimeout(() => cmdRef.current?.focus(), 80);
      }
      if (e.key === 'Escape') {
        setShowCmdBar(false);
        setSelectedDoc(null);
        setShowActions(null);
        stopListening();
      }
      if (e.key === '/' && !showCmdBar && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showCmdBar]);

  // ── Filtered + sorted ──
  const filtered = documents.filter(doc => {
    const catMatch =
      activeCategory === 'all' ||
      doc.category === activeCategory ||
      (activeCategory === 'archived' && doc.status === 'archived');
    const q = searchQuery.trim();
    const searchMatch = !q ||
      fuzzyMatch(doc.name, q) ||
      doc.tags.some(t => fuzzyMatch(t, q)) ||
      fuzzyMatch(doc.preview, q);
    return catMatch && searchMatch;
  }).sort((a, b) => {
    if (sortBy === 'date') return new Date(b.date).getTime() - new Date(a.date).getTime();
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'size') return parseFloat(b.size) - parseFloat(a.size);
    return 0;
  });

  const getCatCount = (id: string) => {
    if (id === 'all')      return documents.length;
    if (id === 'archived') return documents.filter(d => d.status === 'archived').length;
    return documents.filter(d => d.category === id).length;
  };

  // ── Command bar ──
  const handleCommand = useCallback(() => {
    if (!cmdText.trim()) return;
    setProcessing(true);
    setCmdResponse('');
    setTimeout(() => {
      setCmdResponse(getCommandResponse(cmdText));
      setProcessing(false);
    }, 1100);
  }, [cmdText]);

  // ── Voice STT (Web Speech API) ──
  const stopListening = useCallback(() => {
    if (recognizerRef.current) {
      recognizerRef.current.stop();
      recognizerRef.current = null;
    }
    setIsListening(false);
  }, []);

  const toggleVoice = useCallback(() => {
    if (!SPEECH_SUPPORTED) return;

    if (isListening) {
      stopListening();
      return;
    }

    const recog = new SpeechRecognitionAPI();
    recog.lang           = navigator.language || 'en-US';
    recog.interimResults = true;
    recog.continuous     = false;
    recognizerRef.current = recog;

    recog.onresult = (e: any) => {
      const transcript = Array.from(e.results as SpeechRecognitionResultList)
        .map((r: SpeechRecognitionResult) => r[0].transcript)
        .join('');
      setCmdText(transcript);
    };

    recog.onerror = () => { stopListening(); };
    recog.onend   = () => { setIsListening(false); recognizerRef.current = null; };

    recog.start();
    setIsListening(true);
    cmdRef.current?.focus();
  }, [isListening, stopListening]);

  // ── Re-generate action ──
  const handleRegenerate = async (doc: BriefcaseDoc) => {
    setSelectedDoc(null);
    await regenerateDocument(doc.id);
  };

  // ── Delete action ──
  const handleDelete = async (doc: BriefcaseDoc) => {
    setSelectedDoc(null);
    await deleteDocument(doc.id);
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const S = {
    card: (hov: boolean, fileType?: FileType) => {
      const ftc = fileType ? FILE_TYPE_COLORS[fileType] : null;
      const sideBorder = `1px solid ${hov ? 'var(--bc-card-hover-border)' : 'var(--bc-card-border)'}`;
      return {
        padding: '16px', borderRadius: '14px',
        background: hov ? 'var(--bc-card-hover-bg)' : 'var(--bc-card-bg)',
        borderTop: ftc ? `3px solid ${ftc.accent}` : sideBorder,
        borderRight: sideBorder,
        borderBottom: sideBorder,
        borderLeft: sideBorder,
        cursor: 'pointer', backdropFilter: 'blur(6px)',
        position: 'relative' as const,
        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
        transform: hov ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hov && ftc
          ? `0 8px 30px ${ftc.glow}, 0 2px 8px rgba(0,0,0,.12)`
          : '0 1px 3px rgba(0,0,0,.06)',
      };
    },
    badge: (status: DocStatus) => ({
      fontSize: '10px', padding: '3px 9px 3px 7px', borderRadius: '20px',
      background: STATUS_CFG[status].bg,
      color:      STATUS_CFG[status].text,
      fontWeight: 600, letterSpacing: '0.02em',
      display: 'inline-flex' as const, alignItems: 'center' as const, gap: '5px',
    }),
    badgeDot: (status: DocStatus) => ({
      width: '6px', height: '6px', borderRadius: '50%',
      background: STATUS_CFG[status].text,
      flexShrink: 0 as const,
      animation: status === 'draft' ? 'bc-dotPulse 2s ease-in-out infinite' : 'none',
    }),
    tag: (t: string) => {
      const c = tagColor(t);
      return {
        fontSize: '10px', padding: '2px 7px', borderRadius: '5px',
        background: c.bg, color: c.text,
        fontFamily: "'JetBrains Mono', monospace",
      } as React.CSSProperties;
    },
    actionBtn: (danger = false) => ({
      padding: '9px 0', borderRadius: '10px',
      background: danger ? 'rgba(239,68,68,0.07)' : 'rgba(99,102,241,0.06)',
      border:     `1px solid ${danger ? 'rgba(239,68,68,0.12)' : 'rgba(99,102,241,0.1)'}`,
      color:  danger ? '#f87171' : '#a5b4fc',
      fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit',
      display: 'flex', flexDirection: 'column' as const,
      alignItems: 'center', gap: '4px', transition: 'all 0.18s ease',
    }),
  };

  // ─── Skeleton loader ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: '0 4px', color: '#e2e8f0' }}>
        <style>{`@keyframes bc-shimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }`}</style>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(274px, 1fr))', gap: '13px', marginTop: '80px' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{
              height: '180px', borderRadius: '14px',
              background: 'var(--bc-card-bg)', border: '1px solid var(--bc-card-border)',
              overflow: 'hidden', position: 'relative',
            }}>
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(90deg,transparent,rgba(99,102,241,0.06),transparent)',
                animation: `bc-shimmer 1.4s infinite ${i * 0.15}s`,
              }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100%', color: 'var(--bc-text)',
      fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif",
      position: 'relative', padding: '0 4px',
    }}>
      {/* Fonts: self-hosted via /fonts/fonts.css (European data sovereignty) */}
      <style>{`
        :root {
          --bc-card-bg: rgba(255,255,255,0.8);
          --bc-card-border: rgba(0,0,0,0.06);
          --bc-card-hover-bg: rgba(99,102,241,0.04);
          --bc-card-hover-border: rgba(99,102,241,0.2);
          --bc-input-bg: rgba(255,255,255,0.6);
          --bc-panel-bg: rgba(255,255,255,0.5);
          --bc-modal-bg: rgba(255,255,255,0.98);
          --bc-text: #1e293b;
          --bc-subtext: #64748b;
          --bc-badge-bg: rgba(241,245,249,0.8);
          --bc-ext-bg: rgba(0,0,0,0.05);
          --bc-ext-text: #475569;
          --bc-pill-bg: rgba(99,102,241,0.08);
          --bc-pill-text: #4f46e5;
        }
        html.dark {
          --bc-card-bg: rgba(15,16,25,0.45);
          --bc-card-border: rgba(99,102,241,0.07);
          --bc-card-hover-bg: rgba(99,102,241,0.06);
          --bc-card-hover-border: rgba(99,102,241,0.22);
          --bc-input-bg: rgba(15,16,25,0.7);
          --bc-panel-bg: rgba(15,16,25,0.55);
          --bc-modal-bg: rgba(12,13,22,0.98);
          --bc-text: #e2e8f0;
          --bc-subtext: #94a3b8;
          --bc-badge-bg: rgba(30,41,59,0.5);
          --bc-ext-bg: rgba(30,41,59,0.7);
          --bc-ext-text: #64748b;
          --bc-pill-bg: var(--bc-badge-bg);
          --bc-pill-text: #a5b4fc;
        }
        .bc-scrollbar::-webkit-scrollbar{width:5px}
        .bc-scrollbar::-webkit-scrollbar-track{background:transparent}
        .bc-scrollbar::-webkit-scrollbar-thumb{background:rgba(99,102,241,.25);border-radius:3px}
        @keyframes bc-fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes bc-slideIn{from{opacity:0;transform:translateX(-6px)}to{opacity:1;transform:translateX(0)}}
        @keyframes bc-shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}
        @keyframes bc-modalIn{from{opacity:0;transform:scale(0.96) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes bc-cmdIn{from{opacity:0;transform:translateY(-16px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes bc-pulse{0%,100%{opacity:1}50%{opacity:.4}}
        @keyframes bc-spin{to{transform:rotate(360deg)}}
        @keyframes bc-dotPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(0.75)}}
        @keyframes bc-shimmerBtn{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}
        @keyframes bc-dropIn{from{opacity:0;transform:translateY(-8px) scale(0.96)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes bc-focusGlow{0%,100%{box-shadow:0 0 0 2px rgba(99,102,241,.15)}50%{box-shadow:0 0 0 4px rgba(99,102,241,.25)}}
        .bc-catbtn:hover{background:rgba(99,102,241,.08)!important}
        .bc-sortbtn:hover{background:rgba(99,102,241,.12)!important;color:#a5b4fc!important}
        .bc-actionrow:hover{background:var(--bc-pill-bg);color:var(--bc-pill-text)}
        .bc-actionrow:hover .bc-action-icon{transform:scale(1.15);transition:transform 0.15s ease}
        input.bc-input::placeholder{color:rgba(148,163,184,.45)}
        input.bc-input:focus{outline:none;border-color:rgba(99,102,241,.35)!important;box-shadow:0 0 0 3px rgba(99,102,241,.12)!important;animation:bc-focusGlow 2s ease-in-out infinite}
        .bc-new-btn{position:relative;overflow:hidden}
        .bc-new-btn::after{content:'';position:absolute;top:0;left:0;width:40%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.2),transparent);transform:translateX(-100%);}
        .bc-new-btn:hover::after{animation:bc-shimmerBtn 0.8s ease}
      `}</style>

      <div style={{
        opacity:   animateIn ? 1 : 0,
        transform: animateIn ? 'translateY(0)' : 'translateY(18px)',
        transition: 'all 0.55s cubic-bezier(0.4,0,0.2,1)',
      }}>

        {/* ── HEADER ── */}
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          paddingBottom: '18px', borderBottom: '1px solid rgba(99,102,241,.09)', marginBottom: '18px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '11px',
              background: 'linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(99,102,241,.28)',
            }}>
              <FileBox size={20} color="white" />
            </div>
            <div>
              <h1 style={{
                fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em',
                color: 'var(--bc-text)',
              }}>The Briefcase</h1>
              <p style={{ fontSize: '11px', color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: '1px' }}>
                Agent Document Workspace
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button className="bc-new-btn" style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', borderRadius: '9px',
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              border: 'none', color: 'white', fontSize: '13px',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
              boxShadow: '0 2px 12px rgba(99,102,241,.35)',
              transition: 'all 0.2s ease',
            }}>
              <Plus size={14} /> New
            </button>

            <button
              onClick={() => { setShowCmdBar(true); setTimeout(() => cmdRef.current?.focus(), 80); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                padding: '8px 12px', borderRadius: '9px',
                background: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.18)',
                color: '#a5b4fc', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit',
              }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#818cf8', animation: 'bc-pulse 2s ease infinite', flexShrink: 0 }} />
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '11px' }}>Ask Replica</span>
              <span style={{
                padding: '2px 6px', borderRadius: '4px',
                background: 'rgba(99,102,241,.15)', fontSize: '10px',
                fontFamily: "'JetBrains Mono',monospace", color: '#818cf8',
              }}>⌘K</span>
            </button>
          </div>
        </header>

        {/* ── SEARCH + CONTROLS ── */}
        <div style={{ display: 'flex', gap: '9px', alignItems: 'center', marginBottom: '18px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', color: '#475569' }} />
            <input ref={searchRef} className="bc-input" type="text"
              placeholder="Search documents, tags, content…  (press /)"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: '11px 38px',
                borderRadius: '11px', border: '1px solid rgba(99,102,241,.11)',
                background: 'var(--bc-input-bg)', color: 'var(--bc-text)',
                fontSize: '13.5px', fontFamily: 'inherit', backdropFilter: 'blur(8px)',
              }} />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{
                position: 'absolute', right: '11px', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(99,102,241,.14)', border: 'none', color: '#a5b4fc',
                borderRadius: '5px', padding: '2px 7px', fontSize: '10px', cursor: 'pointer',
                fontFamily: "'JetBrains Mono',monospace",
              }}>ESC</button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '3px', background: 'var(--bc-panel-bg)', borderRadius: '10px', padding: '4px', border: '1px solid rgba(99,102,241,.08)' }}>
            <SortDesc size={13} style={{ color: '#475569', margin: '7px 6px 0 6px' }} />
            {(['date','name','size'] as SortKey[]).map(s => (
              <button key={s} className="bc-sortbtn" onClick={() => setSortBy(s)} style={{
                padding: '5px 10px', borderRadius: '7px', border: 'none',
                background: sortBy === s ? 'rgba(99,102,241,.2)' : 'transparent',
                color: sortBy === s ? '#a5b4fc' : '#64748b',
                fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit',
                textTransform: 'capitalize', transition: 'all 0.15s ease',
              }}>{s}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '3px', background: 'var(--bc-panel-bg)', borderRadius: '10px', padding: '4px', border: '1px solid rgba(99,102,241,.08)' }}>
            {([{ m:'grid', Icon:Grid3X3 },{ m:'list', Icon:List }] as { m:ViewMode; Icon:typeof Grid3X3 }[]).map(({ m, Icon }) => (
              <button key={m} onClick={() => setViewMode(m)} style={{
                padding: '6px 8px', borderRadius: '7px', border: 'none',
                background: viewMode === m ? 'rgba(99,102,241,.2)' : 'transparent',
                color: viewMode === m ? '#a5b4fc' : '#64748b',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                transition: 'all 0.15s ease',
              }}><Icon size={14} /></button>
            ))}
          </div>
        </div>

        {/* ── MAIN LAYOUT ── */}
        <div style={{ display: 'flex', gap: '18px', minHeight: 'calc(100vh - 240px)' }}>

          {/* ── SIDEBAR ── */}
          <nav style={{ width: '188px', flexShrink: 0 }}>
            <div style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.11em', color: '#475569', marginBottom: '9px', paddingLeft: '8px', fontWeight: 600 }}>
              Categories
            </div>
            {CATEGORIES.map((cat, i) => {
              const isActive = activeCategory === cat.id;
              return (
                <button key={cat.id} className="bc-catbtn" onClick={() => setActiveCategory(cat.id)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '9px',
                  padding: '8px 9px', borderRadius: '9px', border: 'none',
                  background: isActive ? 'rgba(99,102,241,.12)' : 'transparent',
                  color: isActive ? '#c7d2fe' : 'var(--bc-subtext)',
                  fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit',
                  textAlign: 'left', marginBottom: '2px',
                  borderLeft: `3px solid ${isActive ? '#6366f1' : 'transparent'}`,
                  boxShadow: isActive ? 'inset 0 0 12px rgba(99,102,241,.06)' : 'none',
                  animation: `bc-slideIn 0.28s ease ${i * 0.035}s both`,
                  transition: 'all 0.16s ease',
                }}>
                  <span style={{ color: isActive ? '#818cf8' : '#64748b', display: 'flex', alignItems: 'center', flexShrink: 0 }}>{cat.icon}</span>
                  <span style={{ flex: 1 }}>{cat.label}</span>
                  <span style={{
                    fontSize: '10px', fontFamily: "'JetBrains Mono',monospace",
                    color: isActive ? '#818cf8' : '#475569',
                    background: isActive ? 'rgba(99,102,241,.18)' : 'var(--bc-badge-bg)',
                    padding: '1px 6px', borderRadius: '7px',
                    boxShadow: isActive ? '0 0 8px rgba(99,102,241,.15)' : 'none',
                  }}>{getCatCount(cat.id)}</span>
                </button>
              );
            })}

            {/* Quick Actions */}
            <div style={{ marginTop: '22px', padding: '13px', borderRadius: '11px', background: 'rgba(99,102,241,.04)', border: '1px solid rgba(99,102,241,.08)' }}>
              <div style={{ fontSize: '10px', color: '#818cf8', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Quick Actions</div>
              {[
                { label: 'Upload file',       icon: <Download size={11} /> },
                { label: 'New from template', icon: <Plus     size={11} /> },
                { label: 'Batch export',       icon: <Send     size={11} /> },
                { label: 'Clean duplicates',  icon: <Tag      size={11} /> },
              ].map(({ label, icon }, i) => (
                <div key={label} className="bc-actionrow" style={{
                  padding: '6px 4px', fontSize: '12px', color: 'var(--bc-subtext)', cursor: 'pointer',
                  borderBottom: i < 3 ? '1px solid rgba(99,102,241,.06)' : 'none',
                  display: 'flex', alignItems: 'center', gap: '7px',
                  borderRadius: '5px', transition: 'all 0.15s ease',
                }}>
                  <span style={{ color: '#64748b' }}>{icon}</span>{label}
                </div>
              ))}
            </div>

            {/* Storage */}
            <div style={{ marginTop: '14px', padding: '11px 13px', borderRadius: '11px', background: 'linear-gradient(135deg,rgba(99,102,241,.05),rgba(139,92,246,.05))', border: '1px solid rgba(99,102,241,.09)' }}>
              <div style={{ fontSize: '10px', color: '#818cf8', fontWeight: 600, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Storage</div>
              <div style={{ height: '4px', borderRadius: '2px', background: 'rgba(30,41,59,.8)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, (documents.length / 20) * 100)}%`, borderRadius: '2px', background: 'linear-gradient(90deg,#6366f1,#8b5cf6)', transition: 'width 0.5s ease' }} />
              </div>
              <div style={{ fontSize: '10px', color: '#64748b', marginTop: '5px', fontFamily: "'JetBrains Mono',monospace" }}>
                {documents.length} docs / 100 MB
              </div>
            </div>
          </nav>

          {/* ── DOCUMENTS ── */}
          <main style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '13px' }}>
              <span style={{ fontSize: '12.5px', color: '#64748b' }}>
                {filtered.length} document{filtered.length !== 1 ? 's' : ''}
                {searchQuery && <span style={{ color: '#818cf8' }}> matching "{searchQuery}"</span>}
              </span>
            </div>

            {/* Grid view */}
            {viewMode === 'grid' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(274px,1fr))', gap: '13px' }}>
                {filtered.map((doc, i) => (
                  <div
                    key={doc.id}
                    onClick={() => setSelectedDoc(doc)}
                    onMouseEnter={() => setHoveredId(doc.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{ position: 'relative', zIndex: showActions === doc.id ? 50 : 1, ...S.card(hoveredId === doc.id, doc.type), animation: `bc-fadeUp 0.35s ease ${i * 0.045}s both` }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '9px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: `${FILE_TYPE_COLORS[doc.type].glow}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {FILE_ICONS[doc.type]}
                        </div>
                        <span style={{ fontSize: '9.5px', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', color: 'var(--bc-ext-text)', background: 'var(--bc-ext-bg)', padding: '2px 6px', borderRadius: '4px' }}>.{doc.type}</span>
                      </div>
                      {hoveredId === doc.id || showActions === doc.id ? (
                        <button onClick={e => { e.stopPropagation(); setShowActions(showActions === doc.id ? null : doc.id); }} style={{
                          width: '28px', height: '28px', borderRadius: '8px',
                          background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.2)', color: '#a5b4fc', fontSize: '13px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          transition: 'all 0.15s ease',
                        }}>···</button>
                      ) : (
                        <span style={S.badge(doc.status)}><span style={S.badgeDot(doc.status)} />{STATUS_CFG[doc.status].label}</span>
                      )}
                    </div>

                    <h3 style={{
                      fontSize: '13.5px', fontWeight: 600, color: 'var(--bc-text)', marginBottom: '5px', lineHeight: 1.3,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>{doc.name}</h3>
                    <p style={{
                      fontSize: '11.5px', color: '#64748b', lineHeight: 1.5, marginBottom: '10px',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>{doc.preview}</p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '9px' }}>
                      {doc.tags.slice(0, 3).map(t => <span key={t} style={S.tag(t)}>#{t}</span>)}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '9px', borderTop: '1px solid rgba(99,102,241,.06)' }}>
                      <span style={{ fontSize: '10.5px', color: '#475569', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Clock size={11} color="#475569" />{formatDate(doc.date)}
                      </span>
                      <span style={{ fontSize: '10px', color: '#475569', fontFamily: "'JetBrains Mono',monospace" }}>{doc.size}</span>
                    </div>

                    {/* Overflow dropdown */}
                    {showActions === doc.id && (
                      <div onClick={e => e.stopPropagation()} style={{
                        position: 'absolute', top: '44px', right: '9px', zIndex: 999,
                        background: 'var(--bc-modal-bg)', border: '1px solid var(--bc-card-hover-border)',
                        borderRadius: '12px', padding: '5px', minWidth: '176px',
                        backdropFilter: 'blur(24px)', animation: 'bc-dropIn 0.22s cubic-bezier(0.34,1.56,0.64,1)',
                        boxShadow: '0 12px 40px rgba(0,0,0,.25), 0 0 0 1px rgba(99,102,241,.06)',
                      }}>
                        {[
                          { icon: <Send     size={12} />, label: 'Send'         },
                          { icon: <Copy     size={12} />, label: 'Duplicate'    },
                          { icon: <Link2    size={12} />, label: 'Integrate…'   },
                          { icon: <RefreshCw size={12} />, label: 'Re-generate', action: () => handleRegenerate(doc) },
                          { icon: <Download size={12} />, label: 'Download'     },
                          { icon: <Archive  size={12} />, label: 'Archive', action: () => { updateDocument(doc.id, { status: 'archived' }); setShowActions(null); } },
                        ].map(({ icon, label, action }) => (
                          <div key={label} onClick={action} className="bc-actionrow" style={{
                            padding: '7px 9px', fontSize: '12px', color: 'var(--bc-subtext)',
                            borderRadius: '6px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '8px',
                            transition: 'all 0.13s ease',
                          }}>
                            <span className="bc-action-icon" style={{ color: '#64748b', display: 'flex', transition: 'transform 0.15s ease' }}>{icon}</span> {label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* List view */}
            {viewMode === 'list' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 90px 70px 74px 75px',
                  padding: '7px 13px', fontSize: '9.5px', textTransform: 'uppercase',
                  letterSpacing: '0.09em', color: '#475569', fontWeight: 600,
                }}>
                  <span>Name</span><span>Status</span><span>Type</span><span>Size</span><span>Date</span>
                </div>
                {filtered.map((doc, i) => (
                  <div key={doc.id}
                    onClick={() => setSelectedDoc(doc)}
                    onMouseEnter={() => setHoveredId(doc.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      display: 'grid', gridTemplateColumns: '1fr 90px 70px 74px 75px',
                      padding: '11px 13px', borderRadius: '10px',
                      background: hoveredId === doc.id ? 'rgba(99,102,241,.06)' : 'transparent',
                      border: `1px solid ${hoveredId === doc.id ? 'rgba(99,102,241,.1)' : 'transparent'}`,
                      cursor: 'pointer', alignItems: 'center', position: 'relative', zIndex: showActions === doc.id ? 50 : 1,
                      animation: `bc-slideIn 0.25s ease ${i * 0.025}s both`,
                      transition: 'all 0.15s ease',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                      {FILE_ICONS[doc.type]}
                      <span style={{ fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</span>
                    </div>
                    <span style={S.badge(doc.status)}><span style={S.badgeDot(doc.status)} />{STATUS_CFG[doc.status].label}</span>
                    <span style={{ fontSize: '11px', color: 'var(--bc-ext-text)', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>.{doc.type}</span>
                    <span style={{ fontSize: '11px', color: '#64748b', fontFamily: "'JetBrains Mono',monospace" }}>{doc.size}</span>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>{formatDate(doc.date)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '80px 20px', animation: 'bc-fadeUp 0.4s ease' }}>
                <div style={{ marginBottom: '14px', opacity: 0.3 }}><Search size={46} color="#6366f1" /></div>
                <div style={{ fontSize: '15px', color: '#64748b', marginBottom: '7px' }}>No documents found</div>
                <div style={{ fontSize: '12.5px', color: '#475569' }}>
                  Try a different search, or ask your Replica to create something new.
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          ⌘K COMMAND BAR
          ════════════════════════════════════════════════════════════════════ */}
      {showCmdBar && (
        <div onClick={() => { setShowCmdBar(false); stopListening(); }} style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,.62)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: '14vh',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '568px', borderRadius: '16px',
            background: 'var(--bc-modal-bg)', border: '1px solid rgba(99,102,241,.16)',
            boxShadow: '0 28px 70px rgba(0,0,0,.55),0 0 0 1px rgba(99,102,241,.08)',
            animation: 'bc-cmdIn 0.22s cubic-bezier(0.4,0,0.2,1)', overflow: 'hidden',
          }}>
            {/* Input row */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '15px 17px', gap: '10px', borderBottom: '1px solid rgba(99,102,241,.09)' }}>
              <Sparkles size={16} color="#818cf8" style={{ flexShrink: 0 }} />
              <input ref={cmdRef} className="bc-input" type="text"
                placeholder="Ask your Replica… ('send the proposal', 'compare Q1 reports', 're-generate')"
                value={cmdText} onChange={e => setCmdText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCommand()}
                style={{ flex: 1, background: 'transparent', border: 'none', color: '#e2e8f0', fontSize: '14.5px', fontFamily: 'inherit' }}
              />

              {/* Voice button — only shown if browser supports Web Speech API */}
              {SPEECH_SUPPORTED && (
                <button
                  onClick={toggleVoice}
                  title={isListening ? 'Stop listening' : 'Speak your command'}
                  style={{
                    width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
                    background: isListening ? 'rgba(239,68,68,.15)' : 'rgba(99,102,241,.12)',
                    border:     isListening ? '1px solid rgba(239,68,68,.25)' : '1px solid rgba(99,102,241,.18)',
                    color:  isListening ? '#f87171' : '#a5b4fc',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: isListening ? 'bc-pulse 1.4s infinite' : 'none',
                  }}>
                  {isListening ? <MicOff size={14} /> : <Mic size={14} />}
                </button>
              )}

              {cmdText && (
                <button onClick={handleCommand} style={{
                  padding: '6px 13px', borderRadius: '8px',
                  background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                  border: 'none', color: 'white', fontSize: '12px',
                  cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', flexShrink: 0,
                }}>Run</button>
              )}
            </div>

            {/* Processing shimmer */}
            {processing && (
              <div style={{ padding: '22px', textAlign: 'center' }}>
                <div style={{ width: '200px', height: '3px', borderRadius: '2px', background: 'rgba(99,102,241,.1)', margin: '0 auto', overflow: 'hidden', position: 'relative' }}>
                  <div style={{ position: 'absolute', width: '60px', height: '100%', background: 'linear-gradient(90deg,transparent,#6366f1,transparent)', animation: 'bc-shimmer 1.1s infinite' }} />
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '10px' }}>Replica is thinking…</div>
              </div>
            )}

            {/* Response */}
            {cmdResponse && !processing && (
              <div style={{ padding: '15px 17px', animation: 'bc-fadeUp 0.28s ease' }}>
                <div style={{ padding: '13px', borderRadius: '10px', background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.1)', fontSize: '13px', color: '#c7d2fe', lineHeight: 1.65, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <MessageSquare size={14} color="#818cf8" style={{ flexShrink: 0, marginTop: '2px' }} />
                  {cmdResponse}
                </div>
              </div>
            )}

            {/* Suggestions */}
            {!cmdText && !cmdResponse && (
              <div style={{ padding: '11px 17px 15px' }}>
                <div style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#475569', marginBottom: '8px', fontWeight: 600 }}>Suggestions</div>
                {[
                  { icon: <Send      size={12} />, text: "Send the latest proposal to Marc"          },
                  { icon: <BarChart3 size={12} />, text: "Compare Q1 vs Q4 financial summaries"      },
                  { icon: <Link2     size={12} />, text: "Integrate competitor analysis into pitch"  },
                  { icon: <RefreshCw size={12} />, text: "Re-generate the weekly report with today's data" },
                  { icon: <Sparkles  size={12} />, text: "Summarise my entire Briefcase"             },
                ].map(({ icon, text }) => (
                  <div key={text} onClick={() => setCmdText(text)} className="bc-actionrow" style={{
                    padding: '8px 10px', fontSize: '13px', color: 'var(--bc-subtext)',
                    borderRadius: '8px', cursor: 'pointer', marginBottom: '2px',
                    display: 'flex', alignItems: 'center', gap: '9px',
                    transition: 'all 0.14s ease',
                  }}>
                    <span style={{ color: '#64748b', flexShrink: 0 }}>{icon}</span>
                    {text}
                    <ChevronRight size={12} color="#475569" style={{ marginLeft: 'auto', flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          DOCUMENT DETAIL MODAL
          ════════════════════════════════════════════════════════════════════ */}
      {selectedDoc && (
        <div onClick={() => setSelectedDoc(null)} style={{
          position: 'fixed', inset: 0, zIndex: 180,
          background: 'rgba(0,0,0,.62)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px',
        }}>
          <div onClick={e => e.stopPropagation()} className="bc-scrollbar" style={{
            width: '648px', maxHeight: '82vh', borderRadius: '18px',
            background: 'var(--bc-modal-bg)', border: '1px solid rgba(99,102,241,.13)',
            boxShadow: '0 28px 70px rgba(0,0,0,.5)',
            animation: 'bc-modalIn 0.28s cubic-bezier(0.4,0,0.2,1)', overflowY: 'auto',
          }}>
            <div style={{ padding: '24px' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '19px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {FILE_ICONS_LG[selectedDoc.type]}
                  <div>
                    <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#e2e8f0', marginBottom: '5px' }}>{selectedDoc.name}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={S.badge(selectedDoc.status)}>{STATUS_CFG[selectedDoc.status].label}</span>
                      <span style={{ fontSize: '11px', color: 'var(--bc-subtext)' }}>.{selectedDoc.type} · {selectedDoc.size}</span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelectedDoc(null)} style={{
                  width: '30px', height: '30px', borderRadius: '8px',
                  background: 'rgba(99,102,241,.1)', border: 'none', color: 'var(--bc-subtext)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><X size={16} /></button>
              </div>

              {/* Preview */}
              <div style={{ padding: '14px', borderRadius: '11px', background: 'rgba(99,102,241,.04)', border: '1px solid rgba(99,102,241,.08)', marginBottom: '16px' }}>
                <div style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#818cf8', marginBottom: '7px', fontWeight: 600 }}>Preview</div>
                <p style={{ fontSize: '13px', color: 'var(--bc-text)', lineHeight: 1.72 }}>{selectedDoc.preview}</p>
              </div>

              {/* Conversation Provenance — the key differentiator */}
              <div style={{ padding: '13px', borderRadius: '11px', background: 'linear-gradient(135deg,rgba(99,102,241,.06),rgba(139,92,246,.04))', border: '1px solid rgba(99,102,241,.1)', marginBottom: '16px' }}>
                <div style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#818cf8', marginBottom: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <MessageSquare size={11} color="#818cf8" /> Conversation Provenance
                </div>
                <div style={{ fontSize: '12.5px', color: '#a5b4fc', fontStyle: 'italic', padding: '8px 10px', borderRadius: '7px', background: 'rgba(99,102,241,.07)', borderLeft: '2px solid #6366f1' }}>
                  "{selectedDoc.conversation}"
                </div>
                <div style={{ display: 'flex', gap: '14px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10.5px', color: '#475569', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Clock size={10} color="#64748b" />{selectedDoc.date}
                  </span>
                  <span style={{ fontSize: '10.5px', color: '#475569', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Mic size={10} color="#64748b" />{selectedDoc.origin}
                  </span>
                </div>
              </div>

              {/* Metadata */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                {[
                  { label: 'Category', value: CATEGORIES.find(c => c.id === selectedDoc.category)?.label ?? '—' },
                  { label: 'Status',   value: STATUS_CFG[selectedDoc.status].label },
                ].map(({ label, value }) => (
                  <div key={label} style={{ padding: '10px 11px', borderRadius: '9px', background: 'rgba(30,41,59,.3)', border: '1px solid rgba(99,102,241,.05)' }}>
                    <div style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#475569', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontSize: '12.5px', color: 'var(--bc-subtext)' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Tags */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#475569', marginBottom: '7px', fontWeight: 600 }}>Tags</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {selectedDoc.tags.map(t => <span key={t} style={{ ...S.tag(t), fontSize: '11px', padding: '4px 10px' }}>#{t}</span>)}
                  <span style={{ fontSize: '11px', padding: '4px 9px', borderRadius: '6px', background: 'var(--bc-pill-bg)', color: 'var(--bc-pill-text)', letterSpacing: '0.03em', cursor: 'pointer', border: '1px dashed rgba(99,102,241,.15)' }}>+ Add tag</span>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '7px', paddingTop: '15px', borderTop: '1px solid rgba(99,102,241,.08)' }}>
                {[
                  { icon: <Send      size={16} />, label: 'Send',     danger: false         },
                  { icon: <Download  size={16} />, label: 'Download', danger: false         },
                  { icon: <Link2     size={16} />, label: 'Integrate',danger: false         },
                  {
                    icon: selectedDoc._regenerating
                      ? <RefreshCw size={16} style={{ animation: 'bc-spin 0.9s linear infinite' }} />
                      : <RefreshCw size={16} />,
                    label: selectedDoc._regenerating ? '…' : 'Re-gen',
                    danger: false,
                    onClick: () => handleRegenerate(selectedDoc),
                  },
                  { icon: <Trash2 size={16} />, label: 'Delete', danger: true, onClick: () => handleDelete(selectedDoc) },
                ].map(({ icon, label, danger, onClick }) => (
                  <button key={label} onClick={onClick} style={S.actionBtn(danger)}>
                    {icon}
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

